import type { SearchAction } from "./types"

export const nameMatches = (name: string, searchText: string) =>
  name.toLowerCase().includes(searchText.toLowerCase())

/** Empty query, or `name` or a keyword contains it */
export const matchesNameOrKeywords = (
  { name, keywords }: { name: string; keywords?: readonly string[] },
  query: string,
) => !query || [name, ...(keywords ?? [])].some((text) => nameMatches(text, query))

const matchesPathname = (action: SearchAction, pathname: string) => {
  const condition = action.showWhen?.pathname
  if (!condition) return true
  if (condition.includes && !condition.includes.some((p) => pathname.includes(p))) return false
  if (condition.excludes && condition.excludes.some((p) => pathname.includes(p))) return false
  return true
}

/** Enabled actions for the current page whose name or a keyword matches the query */
export const filterActions = (actions: SearchAction[], pathname: string, query: string) =>
  actions.filter(
    (action) =>
      !action.disabled && matchesPathname(action, pathname) && matchesNameOrKeywords(action, query),
  )

/** Exact (0) → prefix (1) → substring (2) match of `name` on `query`, undefined when it misses */
export const matchRank = (name: string, query: string) => {
  const lowerName = name.toLowerCase()
  const lowerQuery = query.trim().toLowerCase()
  if (lowerName === lowerQuery) return 0
  if (lowerName.startsWith(lowerQuery)) return 1
  if (lowerName.includes(lowerQuery)) return 2
  return undefined
}

const compareIds = (a: number | string, b: number | string) =>
  String(a).localeCompare(String(b), undefined, { numeric: true })

const byNameThenId = <T extends { id: number | string; name: string }>(a: T, b: T) =>
  a.name.localeCompare(b.name) || compareIds(a.id, b.id)

/**
 * Items where any of `names(item)` matches `query`, best match first, then `tieBreak` (name and
 * id by default)
 */
export const rankByName = <T extends { id: number | string; name: string }>(
  items: T[],
  query: string,
  tieBreak: (a: T, b: T) => number = byNameThenId,
  names: (item: T) => string[] = (item) => [item.name],
) =>
  items
    .flatMap((item) => {
      const ranks = names(item)
        .map((name) => matchRank(name, query))
        .filter((rank) => rank !== undefined)
      return ranks.length === 0 ? [] : [{ item, rank: Math.min(...ranks) }]
    })
    .sort((a, b) => a.rank - b.rank || tieBreak(a.item, b.item))
    .map(({ item }) => item)
